"use client";

import { useState, type ReactNode } from "react";
import { useTranslations } from "next-intl";
import { Globe } from "lucide-react";
import { Button } from "@/ui/components/ui/button";
import { Input } from "@/ui/components/ui/input";
import { Label } from "@/ui/components/ui/label";

function TikTokIcon({ className }: { className?: string }) {
	return (
		<svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden>
			<path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
		</svg>
	);
}

function InstagramIcon({ className }: { className?: string }) {
	return (
		<svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden>
			<path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
		</svg>
	);
}

function YouTubeIcon({ className }: { className?: string }) {
	return (
		<svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden>
			<path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
		</svg>
	);
}

function RedditIcon({ className }: { className?: string }) {
	return (
		<svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden>
			<path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 0 0 .029-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 0 0-.232-.095z" />
		</svg>
	);
}

interface FormState {
	// Influencer
	name: string;
	tiktokHandle: string;
	instagramHandle: string;
	youtubeHandle: string;
	redditHandle: string;
	willCreateAccount: boolean;
	// B2B
	firstName: string;
	lastName: string;
	companyName: string;
	phone: string;
	// Shared
	email: string;
	website: string;
	message: string;
}

const initialState: FormState = {
	name: "",
	tiktokHandle: "",
	instagramHandle: "",
	redditHandle: "",
	youtubeHandle: "",
	willCreateAccount: false,
	firstName: "",
	lastName: "",
	companyName: "",
	phone: "",
	email: "",
	website: "",
	message: "",
};

const APPLY_ERROR_CODES = [
	"FIRST_NAME_REQUIRED",
	"LAST_NAME_REQUIRED",
	"EMAIL_INVALID",
	"WEBSITE_INVALID",
	"DUPLICATE_EMAIL",
	"INVALID_APPLICATION",
] as const;

type ApplyErrorCode = (typeof APPLY_ERROR_CODES)[number];

function isApplyErrorCode(value: string): value is ApplyErrorCode {
	return (APPLY_ERROR_CODES as readonly string[]).includes(value);
}

export function AffiliateApplicationForm(props: { initialPartnerType?: "INFLUENCER" | "B2B" }) {
	const t = useTranslations("partners");
	const tErr = useTranslations("partners.applyFormErrors");

	const [formState, setFormState] = useState<FormState>(initialState);
	const [partnerType, setPartnerType] = useState<"INFLUENCER" | "B2B">(
		props.initialPartnerType ?? "INFLUENCER",
	);
	const [error, setError] = useState<string | null>(null);
	const [success, setSuccess] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);

	const translateApiError = (code: string | undefined): string => {
		if (code && isApplyErrorCode(code)) {
			return tErr(code);
		}
		return t("applyFormErrGeneric");
	};

	const updateField = <K extends keyof FormState>(field: K, value: FormState[K]) => {
		setFormState((current) => ({ ...current, [field]: value }));
	};

	const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		setError(null);
		setIsSubmitting(true);

		try {
			const payload =
				partnerType === "INFLUENCER"
					? {
							partnerType,
							name: formState.name,
							email: formState.email,
							tiktokHandle: formState.tiktokHandle,
							instagramHandle: formState.instagramHandle,
							youtubeHandle: formState.youtubeHandle,
							redditHandle: formState.redditHandle,
							website: formState.website,
							willCreateAccount: formState.willCreateAccount,
							message: formState.message,
						}
					: {
							partnerType,
							firstName: formState.firstName,
							lastName: formState.lastName,
							email: formState.email,
							companyName: formState.companyName,
							website: formState.website,
							phone: formState.phone,
							message: formState.message,
						};

			const response = await fetch("/api/affiliate/apply", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify(payload),
			});

			const data = (await response.json()) as { error?: string };

			if (!response.ok) {
				setError(translateApiError(data.error));
				return;
			}

			setSuccess(true);
			setFormState(initialState);
		} catch {
			setError(t("applyFormErrGeneric"));
		} finally {
			setIsSubmitting(false);
		}
	};

	if (success) {
		return (
			<div className="rounded-2xl border border-border bg-card p-6 shadow-sm">
				<h2 className="text-xl font-semibold text-foreground">{t("applyFormSuccessTitle")}</h2>
				<p className="mt-3 text-sm leading-6 text-muted-foreground">{t("applyFormSuccessBody")}</p>
			</div>
		);
	}

	return (
		<form
			onSubmit={handleSubmit}
			className="space-y-6 rounded-2xl border border-border bg-card p-6 shadow-sm"
		>
			<div>
				<h2 className="text-xl font-semibold text-foreground">{t("applyFormTitle")}</h2>
				<p className="mt-2 text-sm leading-6 text-muted-foreground">{t("applyFormSubtitle")}</p>
			</div>

			{error ? (
				<div className="bg-destructive/10 rounded-md p-3 text-sm text-destructive">{error}</div>
			) : null}

			<fieldset className="mb-6">
				<legend className="mb-3 text-sm font-medium text-foreground">{t("applyFormPartnerType")}</legend>
				<div className="flex gap-3">
					{(["INFLUENCER", "B2B"] as const).map((type) => (
						<button
							key={type}
							type="button"
							onClick={() => setPartnerType(type)}
							className={`flex-1 rounded-lg border px-4 py-3 text-sm font-medium transition-colors ${
								partnerType === type
									? "bg-primary/10 border-primary text-primary"
									: "border-border text-muted-foreground hover:border-foreground"
							}`}
						>
							{type === "INFLUENCER" ? t("applyFormTypeInfluencer") : t("applyFormTypeB2b")}
						</button>
					))}
				</div>
			</fieldset>

			{partnerType === "INFLUENCER" ? (
				<InfluencerFields formState={formState} updateField={updateField} t={t} />
			) : (
				<B2BFields formState={formState} updateField={updateField} t={t} />
			)}

			<FormField label={t("applyFormMessage")} htmlFor="message">
				<textarea
					id="message"
					value={formState.message}
					onChange={(event) => updateField("message", event.target.value)}
					rows={6}
					className="focus-visible:outline-hidden flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
				/>
			</FormField>

			<Button type="submit" className="w-full md:w-auto" disabled={isSubmitting}>
				{isSubmitting ? t("applyFormSubmitting") : t("applyFormSubmit")}
			</Button>
		</form>
	);
}

type UpdateField = <K extends keyof FormState>(field: K, value: FormState[K]) => void;
type TFn = ReturnType<typeof useTranslations<"partners">>;

function InfluencerFields({
	formState,
	updateField,
	t,
}: {
	formState: FormState;
	updateField: UpdateField;
	t: TFn;
}) {
	return (
		<div className="space-y-5">
			<div className="grid gap-4 md:grid-cols-2">
				<FormField label={t("applyFormName")} htmlFor="name">
					<Input
						id="name"
						value={formState.name}
						onChange={(e) => updateField("name", e.target.value)}
						placeholder={t("applyFormNamePlaceholder")}
						required
					/>
					<p className="mt-1 text-xs text-muted-foreground">{t("applyFormNameHint")}</p>
				</FormField>
				<FormField label={t("applyFormEmail")} htmlFor="email">
					<Input
						id="email"
						type="email"
						value={formState.email}
						onChange={(e) => updateField("email", e.target.value)}
						required
					/>
				</FormField>
			</div>

			<div className="border-primary/20 bg-primary/5 rounded-xl border p-5">
				<p className="text-sm font-medium text-foreground">{t("applyFormSocialLabel")}</p>
				<p className="mt-1 text-xs text-muted-foreground">{t("applyFormSocialHint")}</p>

				<div className="mt-4 grid gap-3 sm:grid-cols-2">
					<SocialField
						label={t("applyFormTikTok")}
						htmlFor="tiktokHandle"
						value={formState.tiktokHandle}
						onChange={(v) => updateField("tiktokHandle", v)}
						placeholder={t("applyFormHandlePlaceholder")}
						icon={<TikTokIcon className="h-5 w-5" />}
					/>
					<SocialField
						label={t("applyFormInstagram")}
						htmlFor="instagramHandle"
						value={formState.instagramHandle}
						onChange={(v) => updateField("instagramHandle", v)}
						placeholder={t("applyFormHandlePlaceholder")}
						icon={<InstagramIcon className="h-5 w-5" />}
					/>
					<SocialField
						label={t("applyFormYouTube")}
						htmlFor="youtubeHandle"
						value={formState.youtubeHandle}
						onChange={(v) => updateField("youtubeHandle", v)}
						placeholder={t("applyFormHandlePlaceholder")}
						icon={<YouTubeIcon className="h-5 w-5" />}
					/>
					<SocialField
						label={t("applyFormReddit")}
						htmlFor="redditHandle"
						value={formState.redditHandle}
						onChange={(v) => updateField("redditHandle", v)}
						placeholder="u/username"
						icon={<RedditIcon className="h-5 w-5" />}
					/>
					<SocialField
						label={t("applyFormWebsite")}
						htmlFor="influencerWebsite"
						value={formState.website}
						onChange={(v) => updateField("website", v)}
						placeholder="https://"
						icon={<Globe className="h-5 w-5" />}
					/>
				</div>

				<label
					htmlFor="willCreateAccount"
					className="hover:bg-secondary/50 mt-4 flex cursor-pointer items-start gap-3 rounded-lg border border-dashed border-border bg-background p-3"
				>
					<input
						id="willCreateAccount"
						type="checkbox"
						checked={formState.willCreateAccount}
						onChange={(e) => updateField("willCreateAccount", e.target.checked)}
						className="mt-0.5 h-4 w-4 shrink-0"
					/>
					<div>
						<p className="text-sm font-medium text-foreground">{t("applyFormWillCreate")}</p>
						<p className="text-xs text-muted-foreground">{t("applyFormWillCreateHint")}</p>
					</div>
				</label>
			</div>
		</div>
	);
}

function B2BFields({
	formState,
	updateField,
	t,
}: {
	formState: FormState;
	updateField: UpdateField;
	t: TFn;
}) {
	return (
		<div className="grid gap-4 md:grid-cols-2">
			<FormField label={t("applyFormFirstName")} htmlFor="firstName">
				<Input
					id="firstName"
					value={formState.firstName}
					onChange={(e) => updateField("firstName", e.target.value)}
					required
				/>
			</FormField>
			<FormField label={t("applyFormLastName")} htmlFor="lastName">
				<Input
					id="lastName"
					value={formState.lastName}
					onChange={(e) => updateField("lastName", e.target.value)}
					required
				/>
			</FormField>
			<FormField label={t("applyFormEmail")} htmlFor="email">
				<Input
					id="email"
					type="email"
					value={formState.email}
					onChange={(e) => updateField("email", e.target.value)}
					required
				/>
			</FormField>
			<FormField label={t("applyFormCompany")} htmlFor="companyName">
				<Input
					id="companyName"
					value={formState.companyName}
					onChange={(e) => updateField("companyName", e.target.value)}
				/>
			</FormField>
			<FormField label={t("applyFormWebsite")} htmlFor="website">
				<Input
					id="website"
					value={formState.website}
					onChange={(e) => updateField("website", e.target.value)}
					placeholder={t("applyFormWebsitePlaceholder")}
				/>
			</FormField>
			<FormField label={t("applyFormPhone")} htmlFor="phone">
				<Input id="phone" value={formState.phone} onChange={(e) => updateField("phone", e.target.value)} />
			</FormField>
		</div>
	);
}

function SocialField({
	label,
	htmlFor,
	value,
	onChange,
	placeholder,
	icon,
}: {
	label: string;
	htmlFor: string;
	value: string;
	onChange: (v: string) => void;
	placeholder: string;
	icon: ReactNode;
}) {
	return (
		<div className="flex items-center gap-2">
			<span className="mt-6 shrink-0 text-black">{icon}</span>
			<div className="min-w-0 flex-1 space-y-1">
				<Label htmlFor={htmlFor} className="text-xs">
					{label}
				</Label>
				<Input
					id={htmlFor}
					value={value}
					onChange={(e) => onChange(e.target.value)}
					placeholder={placeholder}
					className="text-sm"
				/>
			</div>
		</div>
	);
}

function FormField(props: { htmlFor: string; label: string; children: ReactNode }) {
	return (
		<div className="space-y-2">
			<Label htmlFor={props.htmlFor}>{props.label}</Label>
			{props.children}
		</div>
	);
}
